import { useAuthorizedQuery } from "../api/authorized-query";
import { seedgreenAPI } from "../api/axios";
import { UserRoleDTO, } from "../dtos/UserRoleDTO";
import { STALE_TIME_MEDIUM } from "../queries/query.constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed } from "vue";
const USER_ROLE_STALE_TIME = STALE_TIME_MEDIUM;
export function useCreateUserRole() {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (user) => {
            const response = await seedgreenAPI.post("v2/UserRole", user);
            return response.data; // Backend returns the created role ID
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["user-roles"] });
        },
    });
    return {
        createUserRole: mutation.mutateAsync,
        ...mutation,
    };
}
export function useUserRoles() {
    const query = useQuery({
        enabled: useAuthorizedQuery(),
        queryKey: ["user-roles"],
        queryFn: async () => (await seedgreenAPI.get("v2/UserRole")).data,
        staleTime: USER_ROLE_STALE_TIME,
        select: (data) => {
            try {
                return (data?.map((types) => new UserRoleDTO(types)) ?? []);
            }
            catch (error) {
                console.error("query/user-roles select ERROR", error);
            }
        },
    });
    return {
        userRoles: query.data,
        ...query,
    };
}
export function useUserRole(roleId) {
    const tokenValid = useAuthorizedQuery();
    const query = useQuery({
        enabled: computed(() => tokenValid.value && !!roleId.value),
        queryKey: ["user-role", roleId.value],
        queryFn: async () => (await seedgreenAPI.get(`v2/UserRole/${roleId.value}`)).data,
        staleTime: USER_ROLE_STALE_TIME,
        select: (data) => {
            try {
                if (!data)
                    throw new Error("Role not found");
                return new UserRoleDTO(data);
            }
            catch (error) {
                console.error("query/userRole::select ERROR", error);
            }
        },
    });
    return {
        userRole: query.data,
        ...query,
    };
}
export function useUpdateUserRole() {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (vars) => {
            const { id, role } = vars;
            const response = await seedgreenAPI.put(`v2/UserRole/${id}`, role);
            return response.data;
        },
        onSuccess: async (updatedRole) => {
            await queryClient.invalidateQueries({
                queryKey: ["user-roles"],
            });
            await queryClient.invalidateQueries({
                queryKey: ["user-role", updatedRole.id],
            });
        },
    });
    return {
        updateUserRole: mutation.mutateAsync,
        ...mutation,
    };
}
export function useSetRolePermissions() {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (args) => {
            await seedgreenAPI.put(`v2/UserRole/${args.roleId}/Permissions`, args.permissions);
            return args.roleId;
        },
        onSuccess: async (roleId) => {
            await queryClient.invalidateQueries({
                queryKey: ["user-roles"],
            });
            await queryClient.invalidateQueries({
                queryKey: ["user-role", roleId],
            });
        },
    });
    return {
        setRolePermissions: mutation.mutateAsync,
        ...mutation,
    };
}
export function useDeleteUserRole() {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (roleId) => {
            await seedgreenAPI.delete(`v2/UserRole/${roleId}`);
            return roleId;
        },
        onSuccess: async (roleId) => {
            await queryClient.invalidateQueries({
                queryKey: ["user-roles"],
            });
            await queryClient.invalidateQueries({
                queryKey: ["user-role", roleId],
            });
        },
    });
    return {
        deleteUserRole: mutation.mutateAsync,
        ...mutation,
    };
}
